import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../../Component/Layout';
import { ORDER_API }from "../../apiConfig";

function Dashboard() {
  const [packingData, setPackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedOdId, setSelectedOdId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");


  useEffect(() => {
    fetch(
  `${ORDER_API}/dashboard-od/packing-summary`
)
      .then(res => res.json())
      .then(data => {
        setPackingData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error loading dashboard:", err);
        setLoading(false);
      });
  }, []);

  const dropdownOptions = useMemo(() => {
    const customers = [...new Set(packingData.map(item => item.customer).filter(Boolean))];
    const relevantData = selectedCustomer
      ? packingData.filter(item => item.customer === selectedCustomer)
      : packingData;

    const odIds = [...new Set(relevantData.map(item => item.od_id).filter(Boolean))];

    return { odIds, customers };
  }, [packingData, selectedCustomer]);
  
  const handleCustomerChange = (e) => {
    const newCustomer = e.target.value;
    setSelectedCustomer(newCustomer);
    setSelectedOdId(""); 
  };

  const handleOdIdChange = (e) => {
    const newOdId = e.target.value;
    setSelectedOdId(newOdId);
    if (newOdId) {
      const associatedItem = packingData.find(item => item.od_id === newOdId);
      if (associatedItem && associatedItem.customer) {
        setSelectedCustomer(associatedItem.customer);
      }
    }
  };

  const filteredData = useMemo(() => {
    return packingData.filter(item => {
      const matchOd = selectedOdId === "" || item.od_id === selectedOdId;
      const matchCust = selectedCustomer === "" || item.customer === selectedCustomer;
      return matchOd && matchCust;
    });
  }, [packingData, selectedOdId, selectedCustomer]);


  const groupedData = useMemo(() => {
    const groups = {};
    filteredData.forEach(row => {
      const key = row.od_id || row.so_id;

      if (!groups[key]) {
        groups[key] = {
          uniqueKey: key,
          so_id: row.so_id,
          od_id: row.od_id,
          pi_id: row.pi_id,
          customer: row.customer,
          shippingAddress: row.registered_address, 
          billingAddress: row.communication_address,
          items: []
        };
      }
      groups[key].items.push(row);
    });
    return Object.values(groups);
  }, [filteredData]);

  const toggleRow = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Layout>
      <div style={styles.container}>

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Logistics Overview</h2>
            <p style={styles.subtitle}>Track Deliveries, Picking, and Packing status</p>
          </div>
        </div>

        {/* --- Filter Inputs (Reset Button Removed) --- */}
        <div style={styles.filterCard}>
          <div className="row g-3">
            <div className="col-md-3">
              <label style={styles.filterLabel}>OD ID</label>
              <select 
                className="form-control" 
                style={styles.filterSelect}
                value={selectedOdId}
                onChange={handleOdIdChange}
              >
                <option value="">All OD IDs</option>
                {dropdownOptions.odIds.map(id => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={styles.filterLabel}>Customer</label>
              <select 
                className="form-control" 
                style={styles.filterSelect}
                value={selectedCustomer}
                onChange={handleCustomerChange}
              >
                <option value="">All Customers</option>
                {dropdownOptions.customers.map(cust => (
                  <option key={cust} value={cust}>{cust}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>OD ID</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Shipping Address</th>
                <th style={styles.th}>Billing Address</th>
                <th style={{ ...styles.th, width: '100px', textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={styles.emptyState}>Loading data...</td></tr>
              ) : groupedData.length === 0 ? (
                <tr><td colSpan="5" style={styles.emptyState}>No records match your filters</td></tr>
              ) : (
                groupedData.map((group) => {
                  const isExpanded = expandedRows[group.uniqueKey];

                  return (
                    <React.Fragment key={group.uniqueKey}>
                      <tr style={{ ...styles.tr, backgroundColor: isExpanded ? '#f8fafc' : 'white' }}>
                        <td style={{ ...styles.td, fontWeight: 600, color: '#0f172a' }}>{group.od_id || '-'}</td>
                        <td style={styles.td}>{group.customer || '-'}</td>
                        <td style={styles.td}>
                          <div style={styles.addressCell}>{group.shippingAddress || '-'}</div>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.addressCell}>{group.billingAddress || '-'}</div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button 
                            className="btn btn-dark btn-sm"
                            style={{ fontSize: '12px', padding: '4px 12px' }}
                            onClick={() => toggleRow(group.uniqueKey)}
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan="5" style={{ padding: 0, borderBottom: '1px solid #e2e8f0' }}>
                            <div style={styles.expandedContainer}>
                              <table style={styles.innerTable}>
                                <thead>
                                  <tr>
                                    <th style={styles.innerTh}>PA ID</th>
                                    <th style={styles.innerTh}>Product Code</th>
                                    <th style={styles.innerTh}>Batch No</th>
                                    {/* <th style={styles.innerTh}>Storage Bin</th> */}
                                    <th style={styles.innerTh}>Handling</th>
                                    <th style={{ ...styles.innerTh, textAlign: 'right' }}>Req Qty</th>
                                    {/* <th style={{ ...styles.innerTh, textAlign: 'right' }}>Conf Qty</th> */}
                                    <th style={{ ...styles.innerTh, textAlign: 'right' }}>Del Qty</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.items.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: idx === group.items.length - 1 ? 'none' : '1px solid #eee' }}>
                                      <td style={{ ...styles.innerTd, fontWeight: 600, color: '#0f172a' }}>
                                        {item.pa_id || <span style={{ color: '#cbd5e1' }}>Pending</span>}
                                      </td>
                                      <td style={{ ...styles.innerTd, fontWeight: 500 }}>{item.product_code}</td>
                                      <td style={styles.innerTd}>{item.batch_number || <span style={{ color: '#cbd5e1' }}>N/A</span>}</td>
                                      {/* <td style={styles.innerTd}>{item.storage_bin || <span style={{ color: '#cbd5e1' }}>N/A</span>}</td> */}
                                      <td style={styles.innerTd}>{item.handling || '-'}</td>
                                      <td style={{ ...styles.innerTd, textAlign: 'right' }}>{item.quantity}</td>
                                      {/* <td style={{ ...styles.innerTd, textAlign: 'right' }}>{item.confirmation_qty}</td> */}
                                      <td style={{ ...styles.innerTd, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{item.delivery_qty}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

      </div>
    </Layout>
  );
}

const styles = {
  container: { padding: '10px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748b', marginTop: '4px' },
  filterCard: { background: '#ffffff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '16px', border: '1px solid #e2e8f0' },
  filterLabel: { fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#334155', display: 'block' },
  filterSelect: { height: '36px', fontSize: '13px', borderRadius: '4px', borderColor: '#cbd5e1' },
  tableCard: { background: '#ffffff', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', overflow: 'hidden', border: '1px solid #e2e8f0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { background: '#f8fafc', color: '#475569', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', padding: '12px 16px', borderBottom: '1px solid #e2e8f0', textAlign: 'left' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' },
  td: { padding: '14px 16px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' },
  addressCell: { maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  expandedContainer: { backgroundColor: '#f8fafc', padding: '15px 20px', borderTop: '1px solid #e2e8f0' },
  innerTable: { width: '100%', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0' },
  innerTh: { fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: '700', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f1f5f9' },
  innerTd: { fontSize: '12px', color: '#475569', padding: '8px 12px' },
  emptyState: { padding: '40px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }
};

export default Dashboard;